import type { api } from '@vrplatform/data';
import type { Maybe } from '@vrplatform/utils';
import { createStore, useStore } from 'zustand';
import type { useJournalEntriesTableFilter } from './useJournalEntriesTableFilter';

type QueryFilter = ReturnType<typeof useJournalEntriesTableFilter>['filter'];

type StateFilter = {
  [K in keyof QueryFilter]: QueryFilter[K] | undefined;
};

type JournalEntriesApiFilter = NonNullable<
  api['/reports/journal-entries']['get']['parameters']['query']
>;

export type JournalEntriesModalWhere = {
  transactionIds?: Maybe<string[]>;
  reservationIds?: Maybe<string[]>;
  ownerStatementIds?: Maybe<string[]>;
  journalEntryIds?: Maybe<string[]>;
  filter?: JournalEntriesApiFilter;
};

type State = {
  where: JournalEntriesModalWhere | null;
  filter: StateFilter;
};

type Actions = {
  open: (where: JournalEntriesModalWhere) => void;
  close: () => void;
  setFilter: (filter: Partial<StateFilter>) => void;
};

const initialState: State = {
  where: null,
  filter: {
    search: undefined,
    listingIds: undefined,
    contactIds: undefined,
    reservationIds: undefined,
    party: undefined,
    amount: undefined,
    type: undefined,
    classifications: undefined,
    accountIds: undefined,
    date: undefined,
    status: undefined,
    subType: undefined,
    currency: undefined,
    view: undefined,
    ledger: undefined,
    published: undefined,
    bookingChannels: undefined,
    openingBalanceType: undefined,
    listingOwnershipPeriodIds: undefined,
  },
};

export const journalEntriesModalStore = createStore<State & Actions>(
  (set, get) => ({
    close: () =>
      set({ where: initialState.where, filter: initialState.filter }),
    open: (where) => set({ where }),
    setFilter: (filter) => set({ filter: { ...get().filter, ...filter } }),
    ...initialState,
  })
);

export const useJournalEntriesModal = () => {
  const open = useStore(journalEntriesModalStore, (state) => state.open);
  const close = useStore(journalEntriesModalStore, (state) => state.close);

  return {
    open,
    close,
  };
};

export const useJournalEntriesModalFilter = (): {
  filter: StateFilter;
  setFilter: (filter: Partial<StateFilter>) => void;
} => {
  const filter = useStore(journalEntriesModalStore, (state) => state.filter);
  const setFilter = useStore(
    journalEntriesModalStore,
    (state) => state.setFilter
  );

  return {
    filter,
    setFilter,
  };
};

export const useJournalEntriesModalWhere = () => {
  const where = useStore(journalEntriesModalStore, (state) => state.where);

  return where;
};
